import { useState } from 'react';
import { ArrowUpRight, Copy, RotateCw } from 'lucide-react';
import { TextButton, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useWhatsappGroupActions } from '../../settings/shared/settings-queries';
import { WHATSAPP_PAGE } from './constants';
import styles from './GroupSettingsPanel.module.css';

// The invite-link actions for an ACTIVE group (Figma 14077-197988): Open in WhatsApp · Copy link ·
// Reset link, each the DS `TextButton` (18px — label + trailing 16px action icon, no box), left-aligned
// and parted by full-width hairline dividers. NOT `Button variant="text"`: that is Figma's Ghost *Type*,
// a 30px box with 12px of padding and a hover fill, which drew the rows half as tall again as the
// design's 18px "Text button" instances and needed a negative margin to sit flush with the heading.
// (The old Share row was dropped by design; see the CLAUDE.md divergence note.)
export function InviteLinkRows({ groupId, inviteLink, onToast }: { groupId: string; inviteLink: string; onToast: (message: string) => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { resetInvite } = useWhatsappGroupActions();
  // A reset is in flight. Each click revokes the link Meta-side, so two of them can resolve out of
  // order and leave the REVOKED link stored — a dead link on screen. Same guard on both native apps.
  const [resetting, setResetting] = useState(false);

  const open = () => {
    logPageClickBi({ ...WHATSAPP_PAGE, button_name: 'invite_open' });
    window.open(inviteLink, '_blank', 'noopener,noreferrer');
  };

  const copy = async () => {
    logPageClickBi({ ...WHATSAPP_PAGE, button_name: 'invite_copy' });
    try {
      await navigator.clipboard.writeText(inviteLink);
      onToast(t('settings.whatsapp.group.invite.copied'));
    } catch {
      // Clipboard blocked (no permission / insecure origin) — Open still works.
    }
  };

  const reset = async () => {
    logPageClickBi({ ...WHATSAPP_PAGE, button_name: 'invite_reset' });
    if (resetting) return;
    setResetting(true);
    try {
      await resetInvite(groupId);
      onToast(t('settings.whatsapp.group.invite.reset.done'));
    } catch {
      onToast(t('settings.whatsapp.group.invite.reset.failed'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <section className={styles.inviteSection}>
      <Typography variant="p1" color="strong">
        {t('settings.whatsapp.group.invite')}
      </Typography>
      <div className={styles.linkList}>
        <TextButton size="small" endIcon={<ArrowUpRight size={16} aria-hidden />} onClick={open}>
          {t('settings.whatsapp.group.invite.open')}
        </TextButton>
        <div className={styles.linkDivider} role="presentation" />
        <TextButton size="small" endIcon={<Copy size={16} aria-hidden />} onClick={() => void copy()}>
          {t('settings.whatsapp.group.invite.copy')}
        </TextButton>
        <div className={styles.linkDivider} role="presentation" />
        {/* Disabled in flight — Figma 15378-169154 greys the row behind the toast, which is also what
            makes the re-entrancy guard below visible rather than just silent. */}
        <TextButton size="small" endIcon={<RotateCw size={16} aria-hidden />} disabled={resetting} onClick={() => void reset()}>
          {t('settings.whatsapp.group.invite.reset')}
        </TextButton>
      </div>
    </section>
  );
}
